import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

/**
 * Hybrid auth: JWT (localStorage) + Emergent Google session (httpOnly cookie).
 *
 * Server is the source of truth. On mount we call GET /api/auth/me which
 * accepts EITHER a Bearer JWT or a session_token cookie — whichever is present.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "tracker.auth.token.v1";

const AuthContext = createContext(null);

function readToken() {
  try { return localStorage.getItem(TOKEN_KEY) || null; } catch { return null; }
}
function writeToken(t) {
  try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch {}
}

// FastAPI 422 errors come back as arrays of {msg,...} — flatten safely.
export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Attach Authorization header + cookies to every request when a token is set.
let currentToken = readToken();
axios.interceptors.request.use((config) => {
  if (currentToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${currentToken}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(currentToken);
  // status: 'checking' | 'in' | 'out'
  const [status, setStatus] = useState("checking");
  const [user, setUser] = useState(null);

  const setToken = useCallback((t) => {
    currentToken = t || null;
    writeToken(t);
    setTokenState(t || null);
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, null, { withCredentials: true });
    } catch (_) { /* ignore */ }
    setToken(null);
    setUser(null);
    setStatus("out");
  }, [setToken]);

  // Validate existing session (cookie OR bearer) on mount
  useEffect(() => {
    // CRITICAL: if returning from Google OAuth, AuthCallback will exchange the
    // session_id BEFORE we probe /me — otherwise we race and 401. Skip here.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setStatus("checking");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) {
          setUser(r.data);
          setStatus("in");
        }
      } catch {
        if (!cancelled) {
          if (token) setToken(null);
          setUser(null);
          setStatus("out");
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    setToken(r.data.token);
    setUser(r.data.user);
    setStatus("in");
    return r.data.user;
  }, [setToken]);

  const register = useCallback(async (email, password, inviteCode) => {
    const r = await axios.post(`${API}/auth/register`, {
      email, password, invite_code: inviteCode || "",
    });
    setToken(r.data.token);
    setUser(r.data.user);
    setStatus("in");
    return r.data.user;
  }, [setToken]);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href =
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const exchangeGoogleSession = useCallback(async (sessionId) => {
    const r = await axios.post(
      `${API}/auth/session`,
      null,
      { withCredentials: true, headers: { "X-Session-ID": sessionId } },
    );
    setUser(r.data.user);
    setStatus("in");
    return r.data.user;
  }, []);

  return (
    <AuthContext.Provider value={{
      user, status, login, register, logout,
      loginWithGoogle, exchangeGoogleSession,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
